import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ICollaborationState,
  IComment,
  ICollaborator,
  IAccessControl,
  ICollaborationSession,
  IRealtimeActivity,
  CollaborationRole,
  ShareLevel,
} from "../types/collaboration";
import type { RootState } from "../store";
import collaborationApi, {
  BackendAccessLevel,
  BackendCommentType,
} from "../services/api/collaboration.api";

// Палитра курсоров для presence — детерминированный цвет по userId
const CURSOR_COLORS = [
  "#1976d2",
  "#9c27b0",
  "#2e7d32",
  "#ed6c02",
  "#d32f2f",
  "#0288d1",
  "#7b1fa2",
  "#c2185b",
];

const cursorColorFor = (userId: string): string => {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % CURSOR_COLORS.length;
  return CURSOR_COLORS[index] as string;
};

// CollaborationRole → AccessLevel backend
const roleToAccessLevel = (role: CollaborationRole): BackendAccessLevel => {
  switch (role) {
    case "owner":
      return "owner";
    case "editor":
      return "editor";
    case "commenter":
      return "commenter";
    case "viewer":
    default:
      return "viewer";
  }
};

// Текущий пользователь для thunk-ов (читаем из auth-стора через getState)
interface CurrentUserRef {
  id: string;
  name: string;
  avatar?: string;
}

const currentUserFromState = (state: RootState): CurrentUserRef => {
  const user = state.auth.user;
  return {
    id: user?.id ?? "",
    name: user?.fullName ?? user?.email ?? "Пользователь",
  };
};

// Начальное состояние
const initialState: ICollaborationState = {
  currentSession: null,
  activeUsers: {},
  realtimeActivities: [],

  accessControls: [],
  collaborationRequests: [],

  comments: [],
  commentThreads: {},

  teamTemplates: [],
  templateReviews: {},

  notifications: [],

  versions: [],
  conflicts: [],

  ui: {
    showComments: false,
    showRealtimeUsers: true,
    showVersionHistory: false,
    selectedComment: null,
    commentDraft: "",
    shareDialogOpen: false,
    selectedResource: null,
  },

  loading: {
    session: false,
    comments: false,
    templates: false,
    sharing: false,
  },

  error: null,
};

// Async thunks для API вызовов (REST модуля collaboration, ADR-002/ADR-004)

/**
 * Старт сессии: тянем активных пользователей через REST presence,
 * добавляем текущего пользователя (из auth), собираем ICollaborationSession.
 */
export const startCollaborationSession = createAsyncThunk(
  "collaboration/startSession",
  async (
    {
      resourceId,
      resourceType,
    }: {
      resourceId: string;
      resourceType: "dashboard" | "widget";
    },
    { getState },
  ) => {
    const currentUser = currentUserFromState(getState() as RootState);
    const backendUsers = await collaborationApi.getActiveUsers(
      resourceType,
      resourceId,
    );

    const activeUsers: ICollaborationSession["activeUsers"] = backendUsers.map(
      (u) => {
        const entry: ICollaborationSession["activeUsers"][number] = {
          userId: u.userId,
          userName: u.userId,
          cursorColor: cursorColorFor(u.userId),
          joinedAt: new Date(),
          lastActivity: new Date(u.lastActivity),
        };
        return entry;
      },
    );

    // Гарантируем присутствие текущего пользователя
    if (!activeUsers.some((u) => u.userId === currentUser.id)) {
      const self: ICollaborationSession["activeUsers"][number] = {
        userId: currentUser.id,
        userName: currentUser.name,
        cursorColor: cursorColorFor(currentUser.id),
        joinedAt: new Date(),
        lastActivity: new Date(),
        ...(currentUser.avatar != null && { userAvatar: currentUser.avatar }),
      };
      activeUsers.push(self);
    }

    const session: ICollaborationSession = {
      id: `${resourceType}:${resourceId}`,
      resourceId,
      resourceType,
      activeUsers,
      activities: [],
      startedAt: new Date(),
    };

    return session;
  },
);

/** Загрузка комментариев ресурса через REST. */
export const loadComments = createAsyncThunk(
  "collaboration/loadComments",
  async ({
    resourceId,
    resourceType,
  }: {
    resourceId: string;
    resourceType: "dashboard" | "widget" | "kpi";
  }) => {
    return collaborationApi.getComments(
      resourceId,
      resourceType as BackendCommentType,
    );
  },
);

/** Загрузка шаблонов команд через REST. */
export const loadTeamTemplates = createAsyncThunk(
  "collaboration/loadTeamTemplates",
  async () => {
    return collaborationApi.getTeamTemplates();
  },
);

/** Создание комментария через REST (authorId выводится backend из JWT). */
export const createCommentThunk = createAsyncThunk(
  "collaboration/createComment",
  async ({
    resourceId,
    resourceType,
    content,
    mentions = [],
    parentId,
  }: {
    resourceId: string;
    resourceType: "dashboard" | "widget" | "kpi";
    content: string;
    mentions?: string[];
    parentId?: string;
  }) => {
    return collaborationApi.createComment({
      targetId: resourceId,
      type: resourceType as BackendCommentType,
      content,
      mentions,
      ...(parentId != null && { parentId }),
    });
  },
);

/** Переключение реакции через REST. */
export const toggleReactionThunk = createAsyncThunk(
  "collaboration/toggleReaction",
  async ({
    commentId,
    type,
  }: {
    commentId: string;
    type: "like" | "dislike" | "heart" | "laugh";
  }) => {
    const result = await collaborationApi.toggleReaction(commentId, type);
    return { commentId, reactions: result.reactions };
  },
);

/** Отметить комментарий решённым через REST. */
export const resolveCommentThunk = createAsyncThunk(
  "collaboration/resolveCommentRemote",
  async ({ commentId }: { commentId: string }) => {
    return collaborationApi.resolveComment(commentId);
  },
);

/**
 * Расшаривание ресурса: на каждого коллаборатора — реальный grantAccess,
 * затем собираем единый IAccessControl для стора.
 */
export const shareResource = createAsyncThunk(
  "collaboration/shareResource",
  async (
    {
      resourceId,
      resourceType,
      collaborators,
      shareLevel,
    }: {
      resourceId: string;
      resourceType: "dashboard" | "widget" | "kpi" | "filter" | "template";
      collaborators: Omit<ICollaborator, "id" | "addedAt" | "addedBy">[];
      shareLevel: ShareLevel;
    },
    { getState },
  ) => {
    const currentUser = currentUserFromState(getState() as RootState);
    const granted = await Promise.all(
      collaborators.map(async (c) => {
        const { id } = await collaborationApi.grantAccess({
          resourceId,
          resourceType,
          granteeId: c.userId,
          granteeType: "user",
          level: roleToAccessLevel(c.role),
        });
        const collaborator: ICollaborator = {
          ...c,
          id,
          addedAt: new Date(),
          addedBy: currentUser.id,
        };
        return collaborator;
      }),
    );

    const accessControl: IAccessControl = {
      id: `access_${resourceType}_${resourceId}`,
      resourceType,
      resourceId,
      shareLevel,
      collaborators: granted,
      isPublic: shareLevel === "public",
      requiresApproval: shareLevel === "organization",
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: currentUser.id,
    };

    return accessControl;
  },
);

// Создаем slice
const collaborationSlice = createSlice({
  name: "collaboration",
  initialState,
  reducers: {
    // Управление комментариями
    addComment: (
      state,
      action: PayloadAction<Omit<IComment, "id" | "createdAt" | "updatedAt">>,
    ) => {
      const newComment: IComment = {
        ...action.payload,
        id: `comment_${Date.now()}_${crypto.randomUUID()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      state.comments.push(newComment);

      // Обновляем threads
      const resourceKey = `${newComment.resourceType}_${newComment.resourceId}`;
      const existingThread = state.commentThreads[resourceKey] || [];
      state.commentThreads[resourceKey] = [...existingThread, newComment];
    },

    updateComment: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<IComment> }>,
    ) => {
      const { id, updates } = action.payload;
      const commentIndex = state.comments.findIndex((c) => c.id === id);

      if (commentIndex !== -1) {
        const existing = state.comments[commentIndex];
        if (!existing) {
          return;
        }
        state.comments[commentIndex] = {
          ...existing,
          ...updates,
          id: existing.id,
          updatedAt: new Date(),
          isEdited: true,
          editedAt: new Date(),
          resourceType: existing.resourceType,
        };
      }
    },

    deleteComment: (state, action: PayloadAction<string>) => {
      const commentId = action.payload;
      state.comments = state.comments.filter((c) => c.id !== commentId);

      // Обновляем threads
      Object.keys(state.commentThreads).forEach((key) => {
        const thread = state.commentThreads[key];
        if (!thread) {
          return;
        }
        const updatedThread = thread.filter((c) => c.id !== commentId);
        state.commentThreads[key] = updatedThread;
      });
    },

    resolveComment: (
      state,
      action: PayloadAction<{ id: string; resolvedBy: string }>,
    ) => {
      const { id, resolvedBy } = action.payload;
      const comment = state.comments.find((c) => c.id === id);

      if (comment) {
        comment.status = "resolved";
        comment.resolvedBy = resolvedBy;
        comment.resolvedAt = new Date();
        comment.updatedAt = new Date();
      }
    },

    addCommentReaction: (
      state,
      action: PayloadAction<{
        commentId: string;
        reaction: string;
        userId: string;
      }>,
    ) => {
      const { commentId, reaction, userId } = action.payload;
      const comment = state.comments.find((c) => c.id === commentId);

      if (
        comment &&
        comment.reactions[reaction as keyof typeof comment.reactions]
      ) {
        const reactions =
          comment.reactions[reaction as keyof typeof comment.reactions];
        if (!reactions.includes(userId)) {
          reactions.push(userId);
        }
      }
    },

    removeCommentReaction: (
      state,
      action: PayloadAction<{
        commentId: string;
        reaction: string;
        userId: string;
      }>,
    ) => {
      const { commentId, reaction, userId } = action.payload;
      const comment = state.comments.find((c) => c.id === commentId);

      if (
        comment &&
        comment.reactions[reaction as keyof typeof comment.reactions]
      ) {
        const reactions =
          comment.reactions[reaction as keyof typeof comment.reactions];
        const index = reactions.indexOf(userId);
        if (index > -1) {
          reactions.splice(index, 1);
        }
      }
    },

    // WS: создан комментарий на сервере (comment:created) — upsert по id
    upsertComment: (state, action: PayloadAction<IComment>) => {
      const incoming = action.payload;
      const index = state.comments.findIndex((c) => c.id === incoming.id);
      if (index === -1) {
        state.comments.push(incoming);
      } else {
        state.comments[index] = incoming;
      }

      const resourceKey = `${incoming.resourceType}_${incoming.resourceId}`;
      const thread = state.commentThreads[resourceKey] || [];
      const threadIndex = thread.findIndex((c) => c.id === incoming.id);
      if (threadIndex === -1) {
        state.commentThreads[resourceKey] = [...thread, incoming];
      } else {
        const next = [...thread];
        next[threadIndex] = incoming;
        state.commentThreads[resourceKey] = next;
      }
    },

    // WS/REST: обновлён набор реакций комментария (reaction:updated)
    setCommentReactions: (
      state,
      action: PayloadAction<{
        commentId: string;
        reactions: IComment["reactions"];
      }>,
    ) => {
      const { commentId, reactions } = action.payload;
      const comment = state.comments.find((c) => c.id === commentId);
      if (comment) {
        comment.reactions = reactions;
        comment.updatedAt = new Date();
      }
    },

    // Управление активными пользователями
    addActiveUser: (
      state,
      action: PayloadAction<ICollaborationSession["activeUsers"][0]>,
    ) => {
      if (state.currentSession) {
        const existingUser = state.currentSession.activeUsers.find(
          (u) => u.userId === action.payload.userId,
        );
        if (!existingUser) {
          state.currentSession.activeUsers.push(action.payload);
        }
      }
    },

    removeActiveUser: (state, action: PayloadAction<string>) => {
      if (state.currentSession) {
        state.currentSession.activeUsers =
          state.currentSession.activeUsers.filter(
            (u) => u.userId !== action.payload,
          );
      }
    },

    updateUserActivity: (
      state,
      action: PayloadAction<{ userId: string; activity: IRealtimeActivity }>,
    ) => {
      const { userId, activity } = action.payload;

      if (state.currentSession) {
        const user = state.currentSession.activeUsers.find(
          (u) => u.userId === userId,
        );
        if (user) {
          user.lastActivity = new Date();
        }
      }

      // Добавляем активность в список
      state.realtimeActivities.unshift(activity);

      // Ограничиваем количество активностей
      if (state.realtimeActivities.length > 100) {
        state.realtimeActivities = state.realtimeActivities.slice(0, 100);
      }
    },

    // Управление правами доступа
    updateCollaboratorRole: (
      state,
      action: PayloadAction<{
        accessControlId: string;
        collaboratorId: string;
        newRole: CollaborationRole;
      }>,
    ) => {
      const { accessControlId, collaboratorId, newRole } = action.payload;
      const accessControl = state.accessControls.find(
        (ac) => ac.id === accessControlId,
      );

      if (accessControl) {
        const collaborator = accessControl.collaborators.find(
          (c) => c.id === collaboratorId,
        );
        if (collaborator) {
          collaborator.role = newRole;
          // Обновляем права в зависимости от роли
          switch (newRole) {
            case "owner":
              collaborator.permissions = {
                canEdit: true,
                canComment: true,
                canShare: true,
                canDelete: true,
                canChangeLayout: true,
              };
              break;
            case "editor":
              collaborator.permissions = {
                canEdit: true,
                canComment: true,
                canShare: false,
                canDelete: false,
                canChangeLayout: true,
              };
              break;
            case "commenter":
              collaborator.permissions = {
                canEdit: false,
                canComment: true,
                canShare: false,
                canDelete: false,
                canChangeLayout: false,
              };
              break;
            case "viewer":
              collaborator.permissions = {
                canEdit: false,
                canComment: false,
                canShare: false,
                canDelete: false,
                canChangeLayout: false,
              };
              break;
          }
        }
      }
    },

    removeCollaborator: (
      state,
      action: PayloadAction<{
        accessControlId: string;
        collaboratorId: string;
      }>,
    ) => {
      const { accessControlId, collaboratorId } = action.payload;
      const accessControl = state.accessControls.find(
        (ac) => ac.id === accessControlId,
      );

      if (accessControl) {
        accessControl.collaborators = accessControl.collaborators.filter(
          (c) => c.id !== collaboratorId,
        );
      }
    },

    // UI управление
    setShowComments: (state, action: PayloadAction<boolean>) => {
      state.ui.showComments = action.payload;
    },

    setShowRealtimeUsers: (state, action: PayloadAction<boolean>) => {
      state.ui.showRealtimeUsers = action.payload;
    },

    setShowVersionHistory: (state, action: PayloadAction<boolean>) => {
      state.ui.showVersionHistory = action.payload;
    },

    setSelectedComment: (state, action: PayloadAction<string | null>) => {
      state.ui.selectedComment = action.payload;
    },

    setCommentDraft: (state, action: PayloadAction<string>) => {
      state.ui.commentDraft = action.payload;
    },

    setShareDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.shareDialogOpen = action.payload;
    },

    setSelectedResource: (
      state,
      action: PayloadAction<{ type: string; id: string } | null>,
    ) => {
      state.ui.selectedResource = action.payload;
    },

    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Запуск сессии коллаборации
    builder.addCase(startCollaborationSession.pending, (state) => {
      state.loading.session = true;
      state.error = null;
    });

    builder.addCase(startCollaborationSession.fulfilled, (state, action) => {
      state.loading.session = false;
      state.currentSession = action.payload;
    });

    builder.addCase(startCollaborationSession.rejected, (state, action) => {
      state.loading.session = false;
      state.error = action.error.message || "Ошибка запуска сессии";
    });

    // Загрузка комментариев
    builder.addCase(loadComments.pending, (state) => {
      state.loading.comments = true;
    });

    builder.addCase(loadComments.fulfilled, (state, action) => {
      state.loading.comments = false;
      state.comments = action.payload;

      // Группируем комментарии по ресурсам
      const grouped = action.payload.reduce(
        (acc, comment) => {
          const key = `${comment.resourceType}_${comment.resourceId}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(comment);
          return acc;
        },
        {} as Record<string, IComment[]>,
      );

      Object.entries(grouped).forEach(([key, comments]) => {
        state.commentThreads[key] = comments;
      });
    });

    builder.addCase(loadComments.rejected, (state, action) => {
      state.loading.comments = false;
      state.error = action.error.message || "Ошибка загрузки комментариев";
    });

    // Создание комментария (REST)
    builder.addCase(createCommentThunk.fulfilled, (state, action) => {
      const comment = action.payload;
      if (!state.comments.some((c) => c.id === comment.id)) {
        state.comments.push(comment);
      }
      const resourceKey = `${comment.resourceType}_${comment.resourceId}`;
      const thread = state.commentThreads[resourceKey] || [];
      if (!thread.some((c) => c.id === comment.id)) {
        state.commentThreads[resourceKey] = [...thread, comment];
      }
    });

    builder.addCase(createCommentThunk.rejected, (state, action) => {
      state.error = action.error.message || "Ошибка создания комментария";
    });

    // Переключение реакции (REST)
    builder.addCase(toggleReactionThunk.fulfilled, (state, action) => {
      const { commentId, reactions } = action.payload;
      const comment = state.comments.find((c) => c.id === commentId);
      if (comment) {
        comment.reactions = reactions;
        comment.updatedAt = new Date();
      }
    });

    // Решение комментария (REST)
    builder.addCase(resolveCommentThunk.fulfilled, (state, action) => {
      const resolved = action.payload;
      const index = state.comments.findIndex((c) => c.id === resolved.id);
      if (index !== -1) {
        state.comments[index] = resolved;
      }
    });

    builder.addCase(resolveCommentThunk.rejected, (state, action) => {
      state.error = action.error.message || "Ошибка решения комментария";
    });

    // Загрузка шаблонов команд
    builder.addCase(loadTeamTemplates.pending, (state) => {
      state.loading.templates = true;
    });

    builder.addCase(loadTeamTemplates.fulfilled, (state, action) => {
      state.loading.templates = false;
      state.teamTemplates = action.payload;
    });

    builder.addCase(loadTeamTemplates.rejected, (state, action) => {
      state.loading.templates = false;
      state.error = action.error.message || "Ошибка загрузки шаблонов";
    });

    // Расшаривание ресурса
    builder.addCase(shareResource.pending, (state) => {
      state.loading.sharing = true;
    });

    builder.addCase(shareResource.fulfilled, (state, action) => {
      state.loading.sharing = false;
      state.accessControls.push(action.payload);
    });

    builder.addCase(shareResource.rejected, (state, action) => {
      state.loading.sharing = false;
      state.error = action.error.message || "Ошибка расшаривания ресурса";
    });
  },
});

export const {
  addComment,
  updateComment,
  deleteComment,
  resolveComment,
  addCommentReaction,
  removeCommentReaction,
  upsertComment,
  setCommentReactions,
  addActiveUser,
  removeActiveUser,
  updateUserActivity,
  updateCollaboratorRole,
  removeCollaborator,
  setShowComments,
  setShowRealtimeUsers,
  setShowVersionHistory,
  setSelectedComment,
  setCommentDraft,
  setShareDialogOpen,
  setSelectedResource,
  clearError,
} = collaborationSlice.actions;

export default collaborationSlice.reducer;
